import React from "react";
import { useNavigate } from "react-router-dom";
import { classNames } from "Utils/utils";
import {
  CARE_WATCH_VIEWS,
  CARE_WATCH_STATUSES,
  RISK_BADGE,
  STATUS_LABELS,
  isCareWatchLiveView,
} from "../utils/constants";
import { analyserOverflowLabels } from "../utils/queueUtils";
import {
  adaptLiveParticipant,
  formatLongDate,
  formatPersonName,
  formatTimeRange,
  participantDisplayName,
  participantNameList,
} from "../utils/signalAdapter";
import CareWatchExtraParticipantsCount from "./CareWatchExtraParticipantsCount";
import { CareWatchEmptyDetail } from "./CareWatchEmptyStates";
import { CareWatchSignalDetailBodySkeleton } from "./CareWatchSkeletons";
import CareWatchSignalCheck from "./CareWatchSignalCheck";
import CareWatchFindings from "./CareWatchFindings";
import CareWatchIncidentPreview from "./CareWatchIncidentPreview";
import CareWatchDocCheck from "./CareWatchDocCheck";
import CareWatchTimesheetTools from "./CareWatchTimesheetTools";
import CareWatchLiveDetail from "./CareWatchLiveDetail";
import { IconX } from "./CareWatchIcons";
import { buildTimesheetListHref } from "pages/Rostering/UpdatedTimeSheet/utils/timesheetDeepLink";

const personName = (person) =>
  formatPersonName(
    `${person.firstName || person.first_name || ""} ${
      person.lastName || person.last_name || ""
    }`.trim()
  );

const CareWatchDetailDock = ({
  item,
  view,
  detailLoading = false,
  promoteHighlight,
  promoteLoading = false,
  acknowledgeLoading = false,
  onAcknowledge,
  onPromote,
  onReanalyse,
  reanalyseLoading = false,
  onClose,
}) => {
  const navigate = useNavigate();

  if (!item) {
    return (
      <aside className="care-watch-detail-empty">
        <CareWatchEmptyDetail
          variant={isCareWatchLiveView(view) ? "live" : "signals"}
        />
      </aside>
    );
  }

  if (isCareWatchLiveView(view)) {
    return (
      <CareWatchLiveDetail
        item={item}
        onClose={onClose}
        detailLoading={detailLoading}
      />
    );
  }

  const isDoc = view === CARE_WATCH_VIEWS.DOCUMENTATION;
  const analyserLine = isDoc
    ? analyserOverflowLabels(item.analyser, item.checks)
    : { visible: item.analyser, overflow: [] };
  const people = (item.participants || []).map(adaptLiveParticipant);
  const isMulti = people.length > 1;
  const primary = people[0];
  const risk = RISK_BADGE[item.risk];
  const title = personName(primary) || participantDisplayName(people);
  const allParticipantNames = participantNameList(people);
  const extraParticipantNames = allParticipantNames.slice(1);

  const openTimesheet = () => {
    navigate(
      buildTimesheetListHref({
        timesheetUuid: item.timesheetUuid,
        shiftDate: item.shiftDate,
        workerId: item.workerRef?.id,
      })
    );
  };

  // An incident already written up for this shift. Promoting again would file
  // a duplicate compliance record, so the action is withdrawn rather than
  // merely discouraged. A fuzzy match only warns: it is evidence, not proof.
  const alreadyReported = item?.reportStatus === "reported";
  const maybeReported = item?.reportStatus === "possibly_reported";
  const reportedCount = item?.reportedIncidentCount || 0;
  const isClosed =
    item.status === CARE_WATCH_STATUSES.PROMOTED ||
    item.status === CARE_WATCH_STATUSES.ACKNOWLEDGED;
  // Promote files a new incident — withdrawn once one exists or the signal
  // is already closed. Acknowledge is the open-queue action; mobile hides it
  // on promoted/acknowledged and web was still offering it on the same row.
  const canPromote = !isDoc && !alreadyReported && !isClosed;
  const canAcknowledge = !isClosed && !(alreadyReported && !isDoc);

  const handlePromote = () => {
    onPromote(item);
    // The incident, not merely the shift: a group shift can carry two
    // unrelated events, and the form must fill from the one that was opened.
    const incidentKey = item.incidentKey
      ? `&incident_key=${encodeURIComponent(item.incidentKey)}`
      : "";
    navigate(`/incident-management/create?care_watch=${item.timesheetUuid}${incidentKey}`, {
      // State is kept as a fast path so the form can populate without waiting
      // for a round trip; the `care_watch` query parameter is the durable one
      // and is what survives a refresh or a shared link.
      state: {
        fromCareWatch: true,
        prefill: {
          // The whole signal, so the form fills from one shape whether it came
          // through router state or was re-fetched by the `care_watch`
          // parameter. Passing a hand-picked subset here is how the two paths
          // came to prefill different things.
          careWatchSignal: item,
        },
      },
    });
  };

  return (
    <aside className="care-watch-detail-panel" aria-busy={detailLoading}>
      <header className="care-watch-detail-header">
        <div className="care-watch-detail-head">
          <span className="care-watch-detail-avatar">
            {primary?.initials || "—"}
          </span>
          <div className="care-watch-detail-head-main">
            <div className="care-watch-detail-title-row">
              <h2 className="care-watch-detail-name">{title}</h2>
              {isMulti ? (
                <CareWatchExtraParticipantsCount
                  count={people.length - 1}
                  names={extraParticipantNames}
                  tooltipId={`cw-signal-extra-${item.id}`}
                />
              ) : null}
            </div>
            {!isDoc || isMulti || item.status === CARE_WATCH_STATUSES.ACKNOWLEDGED ? (
              <div className="care-watch-detail-pills">
                {!isDoc ? (
                  <>
                    <span
                      className="care-watch-detail-pill"
                      style={{ background: risk?.bg, color: risk?.text }}
                    >
                      {risk?.label}
                    </span>
                    <span className="care-watch-detail-pill care-watch-detail-pill--status">
                      {STATUS_LABELS[item.status] || item.status}
                    </span>
                  </>
                ) : item.status === CARE_WATCH_STATUSES.ACKNOWLEDGED ? (
                  <span className="care-watch-detail-pill care-watch-detail-pill--status">
                    {STATUS_LABELS[item.status]}
                  </span>
                ) : null}
                {/* Mobile has always carried this and web did not, so the same
                    shift looked like a solo one here. */}
                {isMulti ? (
                  <span className="care-watch-detail-pill care-watch-detail-pill--status">
                    Group shift
                  </span>
                ) : null}
              </div>
            ) : null}
            {/* Joined rather than interpolated with separators: a missing
                worker or shift type used to leave a dangling "·". */}
            <p className="care-watch-detail-meta">
              {[
                formatLongDate(item.shiftDate),
                formatTimeRange(item.workerStartTime, item.workerEndTime),
                item.shiftType,
                typeof item.worker === "string" ? item.worker : item.worker?.name,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="care-watch-detail-close"
              aria-label="Close detail"
            >
              <IconX size={16} />
            </button>
          ) : null}
        </div>
      </header>

      <div className="care-watch-detail-divider" aria-hidden="true" />

      <div className="care-watch-detail-body care-watch-detail-scroll">
        {detailLoading ? (
          <CareWatchSignalDetailBodySkeleton documentation={isDoc} />
        ) : (
        <>
        <CareWatchTimesheetTools
          onOpenTimesheet={openTimesheet}
          onReanalyse={() => onReanalyse && onReanalyse(item)}
          reanalyseLoading={reanalyseLoading}
        />

        <CareWatchSignalCheck
          analyser={analyserLine.visible || item.analyser}
          moreLabels={analyserLine.overflow}
          tooltipId={`cw-analyser-${item.id}`}
          timesheetMarked={item.timesheetMarked}
        />

        {item.preview ? (
          <div className="care-watch-detail-box care-watch-detail-box--note">
            <p className="care-watch-detail-box-title">Preview</p>
            <p className="care-watch-detail-note">{item.preview}</p>
          </div>
        ) : null}

        {isDoc ? (
          <>
            {/* The summary first, then the findings behind it. Mobile has
                always listed the checks; web showed only the summary, so the
                same signal read differently on the two platforms — and on a
                group shift the aggregate note length says far less than
                "no shift note recorded for" each participant. */}
            <CareWatchDocCheck item={item} />
            <CareWatchFindings item={item} showOutcome={false} />
          </>
        ) : (
          <>
            {/* The reasoning first, then the draft it produced -- a manager
                decides on the findings, not on the form preview. */}
            <CareWatchFindings item={item} />
            <CareWatchIncidentPreview
              item={item}
              people={people}
              title={title}
              highlighted={promoteHighlight}
            />
          </>
        )}
        </>
        )}
      </div>

      {item.status === CARE_WATCH_STATUSES.ACKNOWLEDGED &&
      !alreadyReported ? (
        <div
          className="care-watch-detail-reported care-watch-detail-reported--ack"
          role="status"
        >
          Already acknowledged. No incident was filed.
        </div>
      ) : null}

      {!isDoc && (alreadyReported || maybeReported) ? (
        <div
          className={classNames(
            "care-watch-detail-reported",
            alreadyReported
              ? "care-watch-detail-reported--done"
              : "care-watch-detail-reported--maybe"
          )}
          role="status"
        >
          {alreadyReported
            ? `Already reported \u2014 ${reportedCount} incident form${
                reportedCount === 1 ? "" : "s"
              } filed for this shift.`
            : "An incident form may already exist for this shift \u2014 same participant, same time. Check before filing another."}
        </div>
      ) : null}

      {canPromote || canAcknowledge ? (
        <>
          <div className="care-watch-detail-divider" aria-hidden="true" />

          <footer className="care-watch-detail-footer">
            <div className="care-watch-detail-actions">
              {canPromote ? (
                <button
                  type="button"
                  onClick={handlePromote}
                  disabled={promoteLoading}
                  className={classNames(
                    "care-watch-detail-btn care-watch-detail-btn--primary disabled:opacity-50",
                    promoteHighlight && "bg-[#293F6A]"
                  )}
                >
                  {promoteLoading
                    ? "Creating\u2026"
                    : maybeReported
                    ? "Create anyway"
                    : "Create incident"}
                </button>
              ) : null}
              {canAcknowledge ? (
                <button
                  type="button"
                  disabled={acknowledgeLoading}
                  aria-busy={acknowledgeLoading}
                  onClick={() => onAcknowledge(item)}
                  className="care-watch-detail-btn care-watch-detail-btn--secondary gap-2 disabled:opacity-50"
                >
                  {acknowledgeLoading ? (
                    <span className="care-watch-btn-spin" aria-hidden="true" />
                  ) : null}
                  {acknowledgeLoading ? "Saving…" : "Acknowledge"}
                </button>
              ) : null}
            </div>
          </footer>
        </>
      ) : null}
    </aside>
  );
};

export default CareWatchDetailDock;
